import React, { createContext, useState, useEffect } from 'react';

// SERVICES
import { checkLocalCookie, checkSSO, login as authLogin, getSecurityGroups } from '../services/authService';
import { setCurrentUserEmail } from '../api/userIdentity';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);
    const [email, setEmail] = useState(null);
    const [isInternal, setIsInternal] = useState(false);
    const [isExternal, setIsExternal] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [forceReload, setForceReload] = useState(false);

    // Pousse l'email dans le module d'identité partagé avec l'interceptor axios
    // pour que chaque requête sortante porte X-User-Email (utilisé par le
    // tracker de rate limit côté backend).
    useEffect(() => {
        setCurrentUserEmail(email);
    }, [email]);

    useEffect(() => {
        const authenticateUser = async () => {
            try {
                let userEmail = await checkLocalCookie();
                if (!userEmail) {
                    userEmail = await checkSSO();
                }
                
                if (userEmail) {
                    setEmail(userEmail);
                    setAuthenticated(true);

                    // Attendre que fetchGroups termine avant de définir loading à false
                    const [userIsInternal, userIsExternal, userIsAdmin] = await fetchGroups(userEmail);
                    setIsInternal(userIsInternal);
                    setIsExternal(userIsExternal);
                    setIsAdmin(userIsAdmin);

                    // Vérifier si l'utilisateur a l'un des rôles requis
                    if (userIsInternal || userIsExternal || userIsAdmin) {
                        setAuthenticated(true);
                    } else {
                        setAuthenticated(false);
                    }
                } else {
                    setAuthenticated(false);
                }
            } catch (error) {
                console.error("Erreur lors de l'authentification de l'utilisateur :", error);
                setAuthenticated(false);
            } finally {
                setLoading(false);
            }
        };

        authenticateUser();
    }, [forceReload]);

    const fetchGroups = async (email) => {
        const internalGroup = process.env.REACT_APP_GROUP_SECU_INTERNE;
        const externalGroup = process.env.REACT_APP_GROUP_SECU_EXTERNE;
        const adminGroup = process.env.REACT_APP_GROUP_SECU_ADMIN;

        try {
            const groups = await getSecurityGroups(email);

            const userIsInternal = groups.some(group => group.displayName === internalGroup);
            const userIsExternal = groups.some(group => group.displayName === externalGroup);
            const userIsAdmin = groups.some(group => group.displayName === adminGroup);

            return [userIsInternal, userIsExternal, userIsAdmin];
        } catch (error) {
            console.error('Error while fetching groups : ', error);
            return [false, false, false];
        }
    };

    const login = async (email, password) => {
        try {
            const result = await authLogin(email, password);
            
            if (result.success && result.email) {
                const [userIsInternal, userIsExternal, userIsAdmin] = await fetchGroups(result.email);

                setEmail(result.email);
                setAuthenticated(userIsInternal || userIsExternal || userIsAdmin);
                setForceReload(prev => !prev);
                return { success: true, message: "ok" };
            } else {
                setAuthenticated(false);
                // Retourner l'erreur avec le message détaillé
                return { 
                    success: false, 
                    error: result.error || "UNKNOWN_ERROR", 
                    message: result.message || "Une erreur est survenue lors de la connexion." 
                };
            }
        } catch (error) {
            console.error("Erreur lors de la connexion :", error);
            setAuthenticated(false);
            return { 
                success: false, 
                error: "UNKNOWN_ERROR", 
                message: "Une erreur inattendue s'est produite. Veuillez réessayer." 
            };
        }
    };

    const logout = () => {
        // Implémentation de la déconnexion ici
        // Suppression du cookie
        setAuthenticated(false);
        setEmail(null);
        setIsInternal(false);
        setIsExternal(false);
        setIsAdmin(false);
        setForceReload(prev => !prev);
    };

    return (
        <AuthContext.Provider value={{ authenticated, email, isInternal, isExternal, isAdmin, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
}